import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import axios from 'axios';
import InventoryGraph from '../graphs/InventoryGraph';
import SalesGraph from '../graphs/SalesGraph';
import OrdersGraph from '../graphs/OrdersGraph';
import { ArrowLeft } from 'lucide-react';

const RANGE_OPTIONS = ['1W', '1M', '3M', '6M'];

// Shared across all three charts so one control governs the whole page.
export function getRangeStart(range) {
  const now = new Date();
  switch (range) {
    case '1W': return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    case '1M': return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    case '3M': return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    case '6M': return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    default: return new Date(0);
  }
}

function ProductDetailPage() {
  const { id } = useParams();
  const [productData, setProductData] = useState(null);
  const [selectedRange, setSelectedRange] = useState('1M');
  const navigate = useNavigate();

  const rangeStart = useMemo(() => getRangeStart(selectedRange), [selectedRange]);

  useEffect(() => {
    axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/product/${id}`)
      .then(response => setProductData(response.data))
      .catch(error => console.error('Error fetching product data:', error));
  }, [id]);

  if (!productData) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress size={32} thickness={3} />
    </Box>
  );

  return (
    <Box className="fade-in">
      <Button
        onClick={() => navigate('/graphs')}
        startIcon={<ArrowLeft size={16} />}
        sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
      >
        Back to Graphs
      </Button>

      <Box
        sx={{
          position: 'sticky',
          top: '48px',
          zIndex: 5,
          bgcolor: 'background.default',
          py: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            {productData.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Product ID: {id}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Date range · applies to all charts
          </Typography>
          <ToggleButtonGroup
            value={selectedRange}
            exclusive
            onChange={(_, val) => val && setSelectedRange(val)}
            size="small"
          >
            {RANGE_OPTIONS.map((v) => (
              <ToggleButton key={v} value={v} sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>{v}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <InventoryGraph productId={id} rangeStart={rangeStart} />
        </Box>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <SalesGraph inventoryLevels={productData.inventoryLevels} productId={id} rangeStart={rangeStart} />
        </Box>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <OrdersGraph inventoryLevels={productData.inventoryLevels} rangeStart={rangeStart} />
        </Box>
      </Box>
    </Box>
  );
}

export default ProductDetailPage;
